import { createContext, useContext, useEffect, useMemo, useState } from "react";
import * as authService from "@/services/auth";
import {
  AUTH_TOKEN_KEY,
  USER_DATA_KEY,
  clearAuthSession,
  dispatchStorageEvent,
} from "@/utils/auth";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // بررسی session ذخیره شده برای همه مسیرها، از جمله login
    // تا کاربر لاگین‌شده با باز کردن /login دوباره وارد برنامه شود.
    const storedUser = localStorage.getItem(USER_DATA_KEY);
    const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
    
    if (storedUser && storedToken) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
      } catch (error) {
        console.error("Error parsing stored user data:", error);
        localStorage.removeItem(USER_DATA_KEY);
        localStorage.removeItem(AUTH_TOKEN_KEY);
      }
    }
    
    setLoading(false);
  }, []);

  useEffect(() => {
    const syncAuthState = (nextToken) => {
      if (!nextToken) {
        setUser(null);
        return;
      }

      const storedUser = localStorage.getItem(USER_DATA_KEY);
      if (!storedUser) return;

      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error("Error parsing stored user data during sync:", error);
        clearAuthSession();
        setUser(null);
      }
    };

    const handleStorageChange = (event) => {
      if (event.key === AUTH_TOKEN_KEY) {
        syncAuthState(event.newValue);
      }
    };

    const handleCustomStorageChange = (event) => {
      if (event.detail?.key === AUTH_TOKEN_KEY) {
        syncAuthState(event.detail.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("localStorageChange", handleCustomStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("localStorageChange", handleCustomStorageChange);
    };
  }, []);

  /**
   * Login with username and password
   */
  const login = async ({ username, password }) => {
    try {
      const userData = await authService.login({ username, password });
      
      // ذخیره token و user data
      if (userData.token) {
        localStorage.setItem(AUTH_TOKEN_KEY, userData.token);
        dispatchStorageEvent(AUTH_TOKEN_KEY, userData.token);
      }
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
      
      setUser(userData);
      return userData;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Register with mobile number
   */
  const mobileRegister = async ({ phoneNumber }) => {
    try {
      const response = await authService.mobileRegister({ phoneNumber });
      return response; // Usually contains token for verification
    } catch (error) {
      throw error;
    }
  };

  /**
   * Login with mobile number (sends OTP)
   */
  const mobileLogin = async ({ phoneNumber }) => {
    try {
      const response = await authService.mobileLogin({ phoneNumber });
      return response; // Usually contains token for verification
    } catch (error) {
      throw error;
    }
  };

  /**
   * Verify login with token and code (OTP verification)
   */
  const verifyLogin = async ({ token, code }) => {
    try {
      const userData = await authService.verifyLogin({ token, code });
      
      // ذخیره token و user data
      // API returns verificationToken which should be used as auth token
      if (userData.verificationToken) {
        localStorage.setItem(AUTH_TOKEN_KEY, userData.verificationToken);
        dispatchStorageEvent(AUTH_TOKEN_KEY, userData.verificationToken);
      }
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
      
      setUser(userData);
      return userData;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Verify register with token and code (OTP verification for registration)
   */
  const verifyRegister = async ({ token, code }) => {
    try {
      const response = await authService.verifyRegister({ token, code });
      
      // ذخیره token و user data
      // API returns verificationToken which should be used as auth token
      if (response.verificationToken) {
        localStorage.setItem(AUTH_TOKEN_KEY, response.verificationToken);
        dispatchStorageEvent(AUTH_TOKEN_KEY, response.verificationToken);
      }
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(response));
      
      setUser(response);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      clearAuthSession();
      setUser(null);
    }
  };

  const value = useMemo(() => ({
    isAuthenticated: !!user,
    user,
    loading,
    login,
    mobileRegister,
    mobileLogin,
    verifyLogin,
    verifyRegister,
    logout
  }), [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
